type MetricPrimitive = string | number | boolean;

export type MetricAttributes = Record<
  string,
  MetricPrimitive | null | undefined
>;

type NormalizedMetricAttributes = Record<string, MetricPrimitive>;

type SentryMetricsClient = {
  metrics: {
    count: (
      name: string,
      value: number,
      options?: {
        attributes?: NormalizedMetricAttributes;
      },
    ) => void;
    distribution: (
      name: string,
      value: number,
      options?: {
        unit?: string;
        attributes?: NormalizedMetricAttributes;
      },
    ) => void;
  };
  startInactiveSpan: (options: {
    name: string;
    op: string;
    parentSpan: null;
    forceTransaction: boolean;
    startTime: Date;
    attributes: Record<string, string>;
  }) =>
    | {
        end: (endTime?: Date) => void;
      }
    | null
    | undefined;
};

const MAX_ATTRIBUTE_LENGTH = 120;
const MIN_SPAN_DURATION_MS = 1;
const COUNTER_SPAN_OP = 'portal.metric.counter';
const DURATION_SPAN_OP = 'portal.metric.duration';

function toMetricPrimitive(value: unknown): MetricPrimitive | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalized = value.replace(/\s+/g, ' ').trim();
    if (!normalized) {
      return undefined;
    }
    return normalized.slice(0, MAX_ATTRIBUTE_LENGTH);
  }

  if (value == null) {
    return undefined;
  }

  return String(value).slice(0, MAX_ATTRIBUTE_LENGTH);
}

function normalizeAttributes(
  attributes?: MetricAttributes,
): NormalizedMetricAttributes | undefined {
  if (!attributes) {
    return undefined;
  }

  const normalized: NormalizedMetricAttributes = {};

  for (const [key, value] of Object.entries(attributes)) {
    const normalizedValue = toMetricPrimitive(value);
    if (normalizedValue !== undefined) {
      normalized[key] = normalizedValue;
    }
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

function toSpanAttributeValue(value: MetricPrimitive): string {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '0';
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  return value;
}

export function normalizeMetricKey(value: string | undefined): string {
  if (!value) {
    return 'unknown';
  }

  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9._-]/g, '_')
      .replace(/_+/g, '_')
      .slice(0, MAX_ATTRIBUTE_LENGTH) || 'unknown'
  );
}

function toSpanAttributes(
  metricName: string,
  attributes?: NormalizedMetricAttributes,
): Record<string, string> {
  const normalized: Record<string, string> = {
    metric_name: normalizeMetricKey(metricName),
  };

  if (!attributes) {
    return normalized;
  }

  for (const [key, value] of Object.entries(attributes)) {
    if (key === 'metric_name') {
      continue;
    }
    normalized[key] = toSpanAttributeValue(value);
  }

  return normalized;
}

function recordMetricSpan(
  sentry: SentryMetricsClient,
  options: {
    metricName: string;
    op: string;
    durationMs: number;
    attributes?: NormalizedMetricAttributes;
  },
): void {
  const spanDurationMs = Math.max(options.durationMs, MIN_SPAN_DURATION_MS);
  const now = Date.now();

  try {
    const span = sentry.startInactiveSpan({
      name: options.metricName,
      op: options.op,
      parentSpan: null,
      forceTransaction: true,
      startTime: new Date(now - spanDurationMs),
      attributes: toSpanAttributes(options.metricName, options.attributes),
    });
    span?.end(new Date(now));
  } catch {
    // Span telemetry must never interfere with runtime behavior.
  }
}

export function createSentryMetrics(sentry: SentryMetricsClient) {
  function trackCount(
    name: string,
    value = 1,
    attributes?: MetricAttributes,
  ): void {
    const normalizedAttributes = normalizeAttributes(attributes);

    try {
      sentry.metrics.count(name, value, {
        attributes: normalizedAttributes,
      });
    } catch {
      // Metrics must never interfere with runtime behavior.
    }

    recordMetricSpan(sentry, {
      metricName: name,
      op: COUNTER_SPAN_OP,
      durationMs: MIN_SPAN_DURATION_MS,
      attributes: {
        ...(normalizedAttributes ?? {}),
        metric_type: 'counter',
        metric_value: value,
      },
    });
  }

  function trackDuration(
    name: string,
    durationMs: number,
    attributes?: MetricAttributes,
  ): void {
    if (!Number.isFinite(durationMs)) {
      return;
    }

    const safeDurationMs = Math.max(durationMs, 0);
    const normalizedAttributes = normalizeAttributes(attributes);

    try {
      sentry.metrics.distribution(name, safeDurationMs, {
        unit: 'millisecond',
        attributes: normalizedAttributes,
      });
    } catch {
      // Metrics must never interfere with runtime behavior.
    }

    recordMetricSpan(sentry, {
      metricName: name,
      op: DURATION_SPAN_OP,
      durationMs: safeDurationMs,
      attributes: {
        ...(normalizedAttributes ?? {}),
        metric_type: 'duration',
        metric_value: safeDurationMs,
      },
    });
  }

  return {
    trackCount,
    trackDuration,
  };
}
